import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus, FileDown, Trash2, Pencil, ShoppingCart, Truck, CheckCircle2, XCircle, Mail, X, BadgeCheck,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { fmtMoney, fmtDate } from "@/lib/format";
import { ActivityLog } from "@/components/ActivityLog";
import { DatePicker } from "@/components/DatePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const PO_STATUSES = ["draft", "sent", "received", "cancelled"];
const PO_CURRENCIES = ["USD", "IDR", "AUD", "EUR", "SGD"];

function StatusPill({ value }) {
  const map = {
    draft: "bg-slate-100 text-slate-700 ring-slate-200",
    sent: "bg-amber-50 text-amber-800 ring-amber-200",
    received: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    cancelled: "bg-red-50 text-red-700 ring-red-200",
  };
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-md text-[11px] font-medium ring-1 ring-inset ${map[value] || map.draft}`}
    >
      {value}
    </span>
  );
}

const emptyGenerateForm = {
  quotation_id: "",
  supplier_id: "",
  expected_date: "",
  notes: "",
};

export default function PurchaseOrders() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("all");
  const [genOpen, setGenOpen] = useState(false);
  const [genForm, setGenForm] = useState(emptyGenerateForm);
  const [detailPo, setDetailPo] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [editPo, setEditPo] = useState(null);
  const [emailPo, setEmailPo] = useState(null);
  const [approvePo, setApprovePo] = useState(null);

  const { data: pos = [], isLoading } = useQuery({
    queryKey: ["purchase-orders"],
    queryFn: () => api.listPurchaseOrders(),
  });
  const { data: suppliers = [] } = useQuery({
    queryKey: ["suppliers"],
    queryFn: () => api.listSuppliers(),
  });
  const { data: quotations = [] } = useQuery({
    queryKey: ["quotations-for-po"],
    queryFn: () => api.listQuotations(),
  });

  const counts = useMemo(() => {
    const c = { all: pos.length, draft: 0, sent: 0, received: 0, cancelled: 0 };
    for (const p of pos) c[p.status] = (c[p.status] || 0) + 1;
    return c;
  }, [pos]);

  const filtered = pos.filter((p) => filter === "all" || p.status === filter);

  const genMut = useMutation({
    mutationFn: (body) => api.createPurchaseOrderFromQuotation(body),
    onSuccess: (po) => {
      qc.invalidateQueries({ queryKey: ["purchase-orders"] });
      setGenOpen(false);
      setGenForm(emptyGenerateForm);
      toast.success(`Created ${po.po_number}`);
      setDetailPo(po);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Generation failed"),
  });

  const statusMut = useMutation({
    mutationFn: ({ id, status }) => api.updatePurchaseOrderStatus(id, status),
    onSuccess: (po) => {
      qc.invalidateQueries({ queryKey: ["purchase-orders"] });
      qc.invalidateQueries({ queryKey: ["items"] });
      qc.invalidateQueries({ queryKey: ["licences"] });
      qc.invalidateQueries({ queryKey: ["licence-summary"] });
      if (detailPo && detailPo.id === po.id) setDetailPo(po);
      if (po.licence_extended) {
        toast.success(
          `Marked as ${po.status} — licence "${po.licence_extended.product_name}" auto-extended to ${po.licence_extended.new_expiry}`,
          { duration: 8000 },
        );
      } else {
        toast.success(`Marked as ${po.status}`);
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Update failed"),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => api.deletePurchaseOrder(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["purchase-orders"] });
      setConfirmDeleteId(null);
      toast.success("Purchase order deleted");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  const submitGenerate = () => {
    if (!genForm.quotation_id) {
      toast.error("Pick a quotation");
      return;
    }
    if (!genForm.supplier_id) {
      toast.error("Pick a supplier");
      return;
    }
    genMut.mutate(genForm);
  };

  return (
    <div data-testid="purchase-orders-page">
      <PageHeader
        overline="procurement"
        title="Purchase Orders"
        actions={
          <Button
            data-testid="po-generate-btn"
            onClick={() => setGenOpen(true)}
            className="bg-primary hover:bg-primary/90"
          >
            <Plus className="size-4 mr-1.5" /> Generate from quotation
          </Button>
        }
      />

      {/* KPI ribbon */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <KPI testid="po-kpi-total" label="Total POs" icon={ShoppingCart} value={counts.all} tone="slate" />
        <KPI testid="po-kpi-draft" label="Draft" icon={Pencil} value={counts.draft || 0} tone="slate" />
        <KPI testid="po-kpi-sent" label="Sent" icon={Truck} value={counts.sent || 0} tone="amber" />
        <KPI testid="po-kpi-received" label="Received" icon={CheckCircle2} value={counts.received || 0} tone="emerald" />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="po-tabs">
        {[
          { id: "all", label: "All", count: counts.all },
          { id: "draft", label: "Draft", count: counts.draft || 0 },
          { id: "sent", label: "Sent", count: counts.sent || 0 },
          { id: "received", label: "Received", count: counts.received || 0 },
          { id: "cancelled", label: "Cancelled", count: counts.cancelled || 0 },
        ].map((tab) => {
          const active = filter === tab.id;
          return (
            <button
              key={tab.id}
              data-testid={`po-tab-${tab.id}`}
              onClick={() => setFilter(tab.id)}
              className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-medium ring-1 ring-inset transition ${
                active
                  ? "bg-slate-800 text-white ring-slate-800"
                  : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50"
              }`}
            >
              {tab.label}
              <span className={`text-[10px] mono px-1.5 py-0.5 rounded-full ${
                active ? "bg-white/25" : "bg-slate-100 text-slate-600"
              }`}>{tab.count}</span>
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No purchase orders"
          hint="Generate a PO from a client quotation"
          action={
            <Button
              data-testid="po-empty-btn"
              onClick={() => setGenOpen(true)}
              className="bg-primary hover:bg-primary/90"
            >
              <Plus className="size-4 mr-1.5" /> Generate from quotation
            </Button>
          }
        />
      ) : (
        <div className="border border-border rounded-md bg-white overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b border-border">
                <TableHead className="overline">PO #</TableHead>
                <TableHead className="overline">Supplier</TableHead>
                <TableHead className="overline">Quotation</TableHead>
                <TableHead className="overline">Issue</TableHead>
                <TableHead className="overline">Expected</TableHead>
                <TableHead className="overline text-right">Total</TableHead>
                <TableHead className="overline">Status</TableHead>
                <TableHead className="overline text-right w-32">&nbsp;</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((p) => (
                <TableRow
                  key={p.id}
                  data-testid={`po-row-${p.id}`}
                  className="cursor-pointer"
                  onClick={() => setDetailPo(p)}
                >
                  <TableCell className="mono font-medium text-primary">
                    {p.po_number}
                  </TableCell>
                  <TableCell className="font-medium">
                    {p.supplier_snapshot?.name || "—"}
                  </TableCell>
                  <TableCell className="text-xs mono text-muted-foreground">
                    {p.quotation_number ? (
                      <>
                        {p.quotation_number}
                        {p.client_reference && (
                          <div className="text-[10px]">for {p.client_reference}</div>
                        )}
                      </>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell className="mono text-xs">{fmtDate(p.issue_date)}</TableCell>
                  <TableCell className="mono text-xs">
                    {p.expected_date ? fmtDate(p.expected_date) : "—"}
                  </TableCell>
                  <TableCell className="text-right mono font-semibold">
                    {fmtMoney(p.total)} {p.currency}
                  </TableCell>
                  <TableCell>
                    <StatusPill value={p.status} />
                    {p.supplier_confirmation?.confirmed_at && (
                      <div
                        data-testid={`po-supplier-confirmed-${p.id}`}
                        className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200 text-[10px] font-medium"
                        title={`Supplier confirmed by ${p.supplier_confirmation.confirmer_name}`}
                      >
                        <CheckCircle2 className="size-3" />
                        Supplier · {fmtDate(p.supplier_confirmation.delivery_date)}
                      </div>
                    )}
                  </TableCell>
                  <TableCell
                    className="text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="inline-flex gap-1">
                      {["draft", "sent"].includes(p.status) && (
                        <Button
                          data-testid={`po-edit-${p.id}`}
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          title="Edit amounts, currency & details"
                          onClick={() => setEditPo(p)}
                        >
                          <Pencil className="size-3.5" />
                        </Button>
                      )}
                      <Button
                        data-testid={`po-pdf-${p.id}`}
                        variant="ghost"
                        size="sm"
                        className="text-primary h-7 w-7 p-0"
                        title="Download PDF"
                        onClick={() => api.openPurchaseOrderPdf(p.id)}
                      >
                        <FileDown className="size-3.5" />
                      </Button>
                      <Button
                        data-testid={`po-delete-${p.id}`}
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 h-7 w-7 p-0"
                        onClick={() => setConfirmDeleteId(p.id)}
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Generate-from-quotation dialog */}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent data-testid="po-generate-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Generate a Purchase Order</DialogTitle>
            <DialogDescription className="text-xs">
              Pick a client quotation and the supplier that will fulfil it. We&apos;ll
              copy the line items and pre-fill unit_cost from the catalog. You can
              edit costs and tax later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="overline">Quotation *</Label>
              <Select
                value={genForm.quotation_id || undefined}
                onValueChange={(v) => setGenForm({ ...genForm, quotation_id: v })}
              >
                <SelectTrigger data-testid="po-gen-quotation">
                  <SelectValue placeholder="Select a quotation…" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {quotations.length === 0 && (
                    <div className="px-3 py-2 text-xs text-muted-foreground">
                      No quotations available
                    </div>
                  )}
                  {quotations.map((q) => (
                    <SelectItem key={q.id} value={q.id}>
                      {q.quotation_number} · {q.client_snapshot?.name || "—"} ·{" "}
                      {fmtMoney(q.total)} {q.currency}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Supplier *</Label>
              <Select
                value={genForm.supplier_id || undefined}
                onValueChange={(v) => setGenForm({ ...genForm, supplier_id: v })}
              >
                <SelectTrigger data-testid="po-gen-supplier">
                  <SelectValue placeholder="Select a supplier…" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {suppliers.length === 0 && (
                    <div className="px-3 py-2 text-xs text-muted-foreground">
                      No suppliers — add one first in the Suppliers page
                    </div>
                  )}
                  {suppliers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Expected date</Label>
              <DatePicker
                testId="po-gen-expected"
                value={genForm.expected_date}
                onChange={(v) => setGenForm({ ...genForm, expected_date: v })}
                placeholder="Expected delivery"
              />
            </div>
            <div>
              <Label className="overline">Notes</Label>
              <Textarea
                data-testid="po-gen-notes"
                rows={2}
                value={genForm.notes}
                onChange={(e) => setGenForm({ ...genForm, notes: e.target.value })}
                placeholder="e.g. Delivery to KINOS Dili warehouse"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenOpen(false)} data-testid="po-gen-cancel">
              Cancel
            </Button>
            <Button
              data-testid="po-gen-save"
              disabled={genMut.isPending}
              className="bg-primary hover:bg-primary/90"
              onClick={submitGenerate}
            >
              {genMut.isPending ? "Creating…" : "Create PO"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detailPo} onOpenChange={(v) => !v && setDetailPo(null)}>
        <DialogContent data-testid="po-detail-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {detailPo && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <span className="font-mono text-primary">{detailPo.po_number}</span>
                  <StatusPill value={detailPo.status} />
                </DialogTitle>
                <DialogDescription className="text-xs">
                  {detailPo.supplier_snapshot?.name} · Issued {fmtDate(detailPo.issue_date)}
                  {detailPo.expected_date && ` · Expected ${fmtDate(detailPo.expected_date)}`}
                  {detailPo.quotation_number && ` · From quotation ${detailPo.quotation_number}`}
                </DialogDescription>
              </DialogHeader>

              <div className="border border-border rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent border-b border-border">
                      <TableHead className="overline">Item</TableHead>
                      <TableHead className="overline text-right">Qty</TableHead>
                      <TableHead className="overline text-right">Unit cost</TableHead>
                      <TableHead className="overline text-right">Line total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(detailPo.lines || []).map((ln, i) => (
                      <TableRow key={i} data-testid={`po-line-${i}`}>
                        <TableCell className="font-medium">
                          {ln.name}
                          {ln.specification && (
                            <div className="text-[10px] text-muted-foreground line-clamp-2">
                              {ln.specification}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right mono">{ln.qty}</TableCell>
                        <TableCell className="text-right mono">
                          {fmtMoney(ln.unit_cost)} {detailPo.currency}
                        </TableCell>
                        <TableCell className="text-right mono font-semibold">
                          {fmtMoney(ln.line_total)} {detailPo.currency}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex items-center justify-end gap-6 pt-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Subtotal</span>{" "}
                  <span className="mono ml-2">
                    {fmtMoney(detailPo.subtotal)} {detailPo.currency}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Tax</span>{" "}
                  <span className="mono ml-2">
                    {fmtMoney(detailPo.tax)} {detailPo.currency}
                  </span>
                </div>
                <div className="border-l pl-6 border-border">
                  <span className="overline text-primary">Total</span>{" "}
                  <span className="mono ml-2 font-bold text-primary text-lg">
                    {fmtMoney(detailPo.total)} {detailPo.currency}
                  </span>
                </div>
              </div>

              {detailPo.approval?.approver_name && (
                <div
                  data-testid="po-approval-stamp"
                  className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
                >
                  <CheckCircle2 className="size-4 inline mr-1.5 -mt-0.5" />
                  Approved by <b>{detailPo.approval.approver_name}</b>
                  {detailPo.approval.signed_at &&
                    ` on ${fmtDate(detailPo.approval.signed_at.slice(0, 10))}`}
                  {detailPo.approval.notes && (
                    <span className="block text-xs text-emerald-700 mt-0.5">
                      {detailPo.approval.notes}
                    </span>
                  )}
                </div>
              )}

              {detailPo.supplier_confirmation?.confirmed_at && (
                <div
                  data-testid="po-supplier-confirmation"
                  className="rounded-md border border-sky-300 bg-sky-50 px-3 py-2 text-sm text-sky-900"
                >
                  <Truck className="size-4 inline mr-1.5 -mt-0.5" />
                  Supplier confirmed by <b>{detailPo.supplier_confirmation.confirmer_name}</b>
                  {" "}on {fmtDate(detailPo.supplier_confirmation.confirmed_at.slice(0, 10))} —
                  delivery expected <b>{fmtDate(detailPo.supplier_confirmation.delivery_date)}</b>
                  {detailPo.supplier_confirmation.note && (
                    <span className="block text-xs text-sky-700 mt-0.5">
                      {detailPo.supplier_confirmation.note}
                    </span>
                  )}
                </div>
              )}

              {(detailPo.payment_terms || detailPo.supplier_snapshot?.payment_terms) && (
                <div className="rounded-md bg-slate-50 border border-border p-3" data-testid="po-payment-terms">
                  <p className="overline mb-1">Payment terms</p>
                  <p className="text-xs whitespace-pre-wrap">
                    {detailPo.payment_terms || detailPo.supplier_snapshot?.payment_terms}
                  </p>
                </div>
              )}

              {detailPo.notes && (
                <div className="rounded-md bg-slate-50 border border-border p-3">
                  <p className="overline mb-1">Notes</p>
                  <p className="text-xs whitespace-pre-wrap">{detailPo.notes}</p>
                </div>
              )}

              <ActivityLog resourceType="purchase_order" resourceId={detailPo.id} />

              <DialogFooter className="flex-wrap gap-2">
                {!detailPo.approval?.approver_name && detailPo.status !== "cancelled" && (
                  <Button
                    variant="outline"
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                    data-testid="po-detail-approve"
                    onClick={() => setApprovePo(detailPo)}
                  >
                    <BadgeCheck className="size-4 mr-1.5" /> Record approval
                  </Button>
                )}
                {["draft", "sent"].includes(detailPo.status) && (
                  <Button
                    variant="outline"
                    data-testid="po-detail-edit"
                    onClick={() => setEditPo(detailPo)}
                  >
                    <Pencil className="size-4 mr-1.5" /> Edit
                  </Button>
                )}
                {detailPo.status !== "cancelled" && (
                  <Button
                    variant="outline"
                    data-testid="po-detail-email"
                    onClick={() => setEmailPo(detailPo)}
                  >
                    <Mail className="size-4 mr-1.5" /> Email supplier
                  </Button>
                )}
                <Button
                  variant="outline"
                  data-testid="po-detail-pdf"
                  onClick={() => api.openPurchaseOrderPdf(detailPo.id)}
                >
                  <FileDown className="size-4 mr-1.5" /> Download PDF
                </Button>
                {PO_STATUSES.filter((s) => s !== detailPo.status).map((s) => (
                  <Button
                    key={s}
                    data-testid={`po-status-${s}`}
                    variant={s === "received" ? "default" : "outline"}
                    className={
                      s === "received"
                        ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                        : s === "cancelled"
                        ? "text-red-600 border-red-200 hover:bg-red-50"
                        : ""
                    }
                    disabled={statusMut.isPending}
                    onClick={() =>
                      statusMut.mutate({ id: detailPo.id, status: s })
                    }
                  >
                    {s === "sent" && <Truck className="size-4 mr-1.5" />}
                    {s === "received" && <CheckCircle2 className="size-4 mr-1.5" />}
                    {s === "cancelled" && <XCircle className="size-4 mr-1.5" />}
                    Mark {s}
                  </Button>
                ))}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!confirmDeleteId}
        onOpenChange={(v) => !v && setConfirmDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this purchase order?</AlertDialogTitle>
            <AlertDialogDescription>
              Received POs cannot be deleted. Cancel first, then delete.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="po-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="po-delete-confirm"
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteMut.mutate(confirmDeleteId)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <EditPoDialog
        key={editPo?.id || "po-edit-closed"}
        po={editPo}
        onClose={() => setEditPo(null)}
        onSaved={(updated) => {
          qc.invalidateQueries({ queryKey: ["purchase-orders"] });
          setEditPo(null);
          if (detailPo && detailPo.id === updated.id) setDetailPo(updated);
        }}
      />

      <EmailPoDialog
        key={emailPo?.id || "po-email-closed"}
        po={emailPo}
        onClose={() => setEmailPo(null)}
        onSent={(updated) => {
          qc.invalidateQueries({ queryKey: ["purchase-orders"] });
          setEmailPo(null);
          if (detailPo && detailPo.id === updated.id) setDetailPo(updated);
        }}
      />

      <ApprovePoDialog
        key={approvePo?.id || "po-approve-closed"}
        po={approvePo}
        onClose={() => setApprovePo(null)}
        onSaved={(updated) => {
          qc.invalidateQueries({ queryKey: ["purchase-orders"] });
          setApprovePo(null);
          if (detailPo && detailPo.id === updated.id) setDetailPo(updated);
        }}
      />
    </div>
  );
}

function KPI({ testid, label, icon: Icon, value, tone }) {
  const toneMap = {
    slate: "border-slate-200 bg-white",
    amber: "border-amber-200 bg-amber-50",
    emerald: "border-emerald-200 bg-emerald-50",
  };
  const iconMap = {
    slate: "text-slate-700 bg-slate-100",
    amber: "text-amber-800 bg-amber-100",
    emerald: "text-emerald-700 bg-emerald-100",
  };
  return (
    <div
      data-testid={testid}
      className={`rounded-md border p-4 flex items-center justify-between ${toneMap[tone]}`}
    >
      <div>
        <p className="overline">{label}</p>
        <p className="text-2xl font-bold text-foreground mt-1 font-mono">{value}</p>
      </div>
      <span className={`size-10 grid place-items-center rounded-full ${iconMap[tone]}`}>
        <Icon className="size-5" />
      </span>
    </div>
  );
}


function EditPoDialog({ po, onClose, onSaved }) {
  const [form, setForm] = useState(() =>
    po
      ? {
          currency: po.currency || "USD",
          expected_date: po.expected_date || "",
          tax_rate: po.tax_rate ?? 0,
          notes: po.notes || "",
          payment_terms: po.payment_terms || po.supplier_snapshot?.payment_terms || "",
          lines: (po.lines || []).map((l) => ({ ...l })),
        }
      : null,
  );
  const [busy, setBusy] = useState(false);
  if (!po || !form) return null;

  const setLine = (i, patch) => {
    const lines = form.lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l));
    setForm({ ...form, lines });
  };
  const subtotal = form.lines.reduce(
    (s, l) => s + (Number(l.qty) || 0) * (Number(l.unit_cost) || 0), 0,
  );
  const tax = (subtotal * (Number(form.tax_rate) || 0)) / 100;
  const total = subtotal + tax;

  async function save() {
    const lines = form.lines
      .filter((l) => (l.name || "").trim())
      .map((l) => ({
        item_id: l.item_id || null,
        name: l.name,
        specification: l.specification || "",
        brand: l.brand || "",
        model: l.model || "",
        qty: Number(l.qty) || 0,
        unit_cost: Number(l.unit_cost) || 0,
      }));
    if (lines.length === 0) {
      toast.error("At least one line item is required");
      return;
    }
    if (lines.some((l) => l.unit_cost <= 0)) {
      toast.warning("Some lines still have no amount — the PO total will be incomplete");
    }
    setBusy(true);
    try {
      const updated = await api.updatePurchaseOrder(po.id, {
        currency: form.currency,
        expected_date: form.expected_date || null,
        notes: form.notes,
        payment_terms: form.payment_terms.trim(),
        tax_rate: Number(form.tax_rate) || 0,
        lines,
      });
      toast.success(`${updated.po_number} updated — total ${fmtMoney(updated.total)} ${updated.currency}`);
      setForm(null);
      onSaved(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={!!po} onOpenChange={(v) => { if (!v) { setForm(null); onClose(); } }}>
      <DialogContent data-testid="po-edit-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Edit <span className="mono text-primary">{po.po_number}</span>
          </DialogTitle>
          <DialogDescription className="text-xs">
            Adjust quantities, unit costs, currency and tax — totals recalculate on save.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="overline">Currency</Label>
            <Select
              value={form.currency}
              onValueChange={(v) => setForm({ ...form, currency: v })}
            >
              <SelectTrigger data-testid="po-edit-currency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PO_CURRENCIES.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`po-currency-${c}`}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="overline">Expected date</Label>
            <DatePicker
              testId="po-edit-expected"
              value={form.expected_date || ""}
              onChange={(v) => setForm({ ...form, expected_date: v })}
              placeholder="Expected delivery"
            />
          </div>
          <div>
            <Label className="overline">Tax rate (%)</Label>
            <Input
              data-testid="po-edit-tax"
              type="number"
              min="0"
              step="0.1"
              value={form.tax_rate}
              onChange={(e) => setForm({ ...form, tax_rate: e.target.value })}
            />
          </div>
        </div>

        <div className="border border-border rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b border-border">
                <TableHead className="overline">Item</TableHead>
                <TableHead className="overline w-20 text-right">Qty</TableHead>
                <TableHead className="overline w-32 text-right">Unit cost</TableHead>
                <TableHead className="overline w-28 text-right">Line total</TableHead>
                <TableHead className="w-8"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {form.lines.map((l, i) => {
                const lt = (Number(l.qty) || 0) * (Number(l.unit_cost) || 0);
                return (
                  <TableRow key={i} data-testid={`po-edit-line-${i}`}>
                    <TableCell>
                      <Input
                        data-testid={`po-edit-line-name-${i}`}
                        value={l.name}
                        onChange={(e) => setLine(i, { name: e.target.value })}
                        className="h-8 text-sm"
                      />
                      <Input
                        data-testid={`po-edit-line-spec-${i}`}
                        value={l.specification || ""}
                        onChange={(e) => setLine(i, { specification: e.target.value })}
                        className="h-7 text-xs mt-1 text-muted-foreground"
                        placeholder="specification (optional)"
                      />
                    </TableCell>
                    <TableCell className="align-top">
                      <Input
                        data-testid={`po-edit-line-qty-${i}`}
                        type="number"
                        min="0"
                        step="1"
                        value={l.qty}
                        onChange={(e) => setLine(i, { qty: e.target.value })}
                        className="h-8 text-sm text-right mono"
                      />
                    </TableCell>
                    <TableCell className="align-top">
                      <Input
                        data-testid={`po-edit-line-cost-${i}`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={l.unit_cost}
                        onChange={(e) => setLine(i, { unit_cost: e.target.value })}
                        className={`h-8 text-sm text-right mono ${Number(l.unit_cost) <= 0 ? "border-red-300 bg-red-50" : ""}`}
                      />
                    </TableCell>
                    <TableCell className="text-right mono text-sm align-top pt-3.5">
                      {fmtMoney(lt)}
                    </TableCell>
                    <TableCell className="align-top pt-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-red-600"
                        data-testid={`po-edit-line-remove-${i}`}
                        onClick={() =>
                          setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) })
                        }
                      >
                        <X className="size-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <div className="px-3 py-2 border-t border-border">
            <Button
              variant="ghost"
              size="sm"
              data-testid="po-edit-add-line"
              onClick={() =>
                setForm({
                  ...form,
                  lines: [...form.lines, { name: "", specification: "", qty: 1, unit_cost: 0 }],
                })
              }
            >
              <Plus className="size-3.5 mr-1" /> Add line
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-end gap-6 text-sm">
          <span className="text-muted-foreground">Subtotal <span className="mono ml-1">{fmtMoney(subtotal)}</span></span>
          <span className="text-muted-foreground">Tax <span className="mono ml-1">{fmtMoney(tax)}</span></span>
          <span className="font-bold text-primary" data-testid="po-edit-total">
            Total <span className="mono ml-1 text-lg">{fmtMoney(total)} {form.currency}</span>
          </span>
        </div>

        <div>
          <Label className="overline">Payment terms</Label>
          <Input
            data-testid="po-edit-payment-terms"
            value={form.payment_terms}
            onChange={(e) => setForm({ ...form, payment_terms: e.target.value })}
            placeholder="e.g. 50% deposit, balance on delivery — Net 30"
          />
          <p className="text-[10px] text-muted-foreground mt-1">
            Prints on the PO PDF. Pre-filled from the supplier&apos;s default terms.
          </p>
        </div>

        <div>
          <Label className="overline">Notes</Label>
          <Textarea
            data-testid="po-edit-notes"
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </div>

        <DialogFooter>
          <Button data-testid="po-edit-cancel" variant="outline" onClick={() => { setForm(null); onClose(); }}>Cancel</Button>
          <Button
            data-testid="po-edit-save"
            className="bg-primary hover:bg-primary/90"
            disabled={busy}
            onClick={save}
          >
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApprovePoDialog({ po, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  if (!po) return null;

  async function save() {
    if (!name.trim()) {
      toast.error("Approver name is required");
      return;
    }
    setBusy(true);
    try {
      const updated = await api.approvePurchaseOrder(po.id, {
        approver_name: name.trim(),
        signed_date: date,
        notes: notes.trim(),
      });
      toast.success(`${updated.po_number} approved by ${name.trim()}`);
      onSaved(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approval failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={!!po} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="po-approve-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            Record approval for <span className="mono text-primary">{po.po_number}</span>
          </DialogTitle>
          <DialogDescription className="text-xs">
            The manager approval stamp will appear on the PO detail and print on
            the PDF so the supplier sees it&apos;s authorised.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="overline">Approving manager *</Label>
            <Input
              data-testid="po-approve-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Maria Gomes"
            />
          </div>
          <div>
            <Label className="overline">Approval date</Label>
            <DatePicker
              testId="po-approve-date"
              value={date}
              onChange={(v) => setDate(v || new Date().toISOString().slice(0, 10))}
              clearable={false}
            />
          </div>
          <div>
            <Label className="overline">Note (optional)</Label>
            <Textarea
              data-testid="po-approve-notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Approved within Q3 budget"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="po-approve-cancel">
            Cancel
          </Button>
          <Button
            data-testid="po-approve-save"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            disabled={busy}
            onClick={save}
          >
            <BadgeCheck className="size-4 mr-1.5" /> {busy ? "Saving…" : "Record approval"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmailPoDialog({ po, onClose, onSent }) {
  const [to, setTo] = useState(() => po?.supplier_snapshot?.email || "");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  if (!po) return null;

  async function send() {
    if (!to.trim()) {
      toast.error("Recipient email is required");
      return;
    }
    setBusy(true);
    try {
      const updated = await api.emailPurchaseOrder(po.id, {
        to: to.trim(),
        message: message.trim(),
      });
      toast.success(`${updated.po_number} emailed to ${to.trim()}`);
      onSent(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={!!po} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="po-email-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            Email <span className="mono text-primary">{po.po_number}</span> to supplier
          </DialogTitle>
          <DialogDescription className="text-xs">
            Sends the PO PDF ({fmtMoney(po.total)} {po.currency}) to{" "}
            {po.supplier_snapshot?.name || "the supplier"}. The email includes a
            one-click link for the supplier to confirm receipt and delivery date.
            Draft POs flip to &quot;sent&quot; automatically.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="overline">To</Label>
            <Input
              data-testid="po-email-to"
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="supplier@example.com"
            />
          </div>
          <div>
            <Label className="overline">Message (optional)</Label>
            <Textarea
              data-testid="po-email-message"
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="e.g. Please confirm delivery by end of month."
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            data-testid="po-email-send"
            className="bg-primary hover:bg-primary/90"
            disabled={busy}
            onClick={send}
          >
            <Mail className="size-4 mr-1.5" /> {busy ? "Sending…" : "Send PO"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

